import * as Dialog from "@radix-ui/react-dialog";
import { FileText, Folder, RotateCcw, SquareCheckBig, Trash2, X } from "lucide-react";
import { create } from "zustand";
import { TRASH_RETENTION_DAYS, type WorkspaceItem } from "@hydra-notes/shared";
import { ctxFor, getData, useAllSpaceItems } from "@/data/store";
import { useSharedFolders, sharedAccent } from "@/data/sharedFolders";
import { Button } from "@/ui/Button";
import { IconButton } from "@/ui/IconButton";
import { toast } from "@/ui/toast";
import { openItem } from "@/commands/nav";
import { deleteWorkspaceItemForever } from "@/data/permanentDelete";
import { useUi } from "@/store/ui";
import s from "./TrashDialog.module.css";

interface TrashDialogStore {
  open: boolean;
  setOpen: (open: boolean) => void;
}
export const useTrashDialog = create<TrashDialogStore>((set) => ({
  open: false,
  setOpen: (open) => set({ open }),
}));

/** Subtree roots only (children restore/delete with their parent). */
function trashRoots(items: WorkspaceItem[]): WorkspaceItem[] {
  return items
    .filter((i) => i.trashedAt)
    .filter((i) => {
      const parent = i.parentId ? items.find((p) => p.id === i.parentId) : null;
      return !parent?.trashedAt;
    })
    .sort((a, b) => (b.trashedAt ?? 0) - (a.trashedAt ?? 0));
}

export function TrashDialog() {
  const { open, setOpen } = useTrashDialog();
  const spaces = useAllSpaceItems();
  const folders = useSharedFolders((st) => st.folders);
  const activeApp = useUi((state) => state.activeApp);
  const tasks = activeApp === "tasks";
  const belongsHere = (item: WorkspaceItem) =>
    tasks ? item.type === "tasklist" : item.type !== "card" && item.type !== "cardboard" && item.type !== "tasklist";

  const personal = spaces.find((sp) => sp.kind === "personal");
  // Cards own their archive/trash experience inside the Cards product.
  const personalTrash = personal ? trashRoots(personal.items.filter(belongsHere)) : [];
  // shared trash sections appear only for folders the user OWNS: trash is
  // owner-only in shared folders, other members never see it
  const ownedSections = spaces
    .filter((sp) => sp.kind === "shared")
    .map((sp) => ({ sp, meta: folders.find((f) => f.id === sp.spaceId) }))
    .filter((x) => x.meta?.role === "owner")
    .map((x) => ({ meta: x.meta!, trashed: trashRoots(x.sp.items.filter(belongsHere)) }))
    .filter((x) => x.trashed.length > 0);

  const restore = (spaceId: string | undefined, id: string, title: string) => {
    const ctx = spaceId ? ctxFor(spaceId) : getData();
    if (!ctx) return;
    ctx.workspace.restore(id);
    toast.success(`Restored "${title || "Untitled"}"`, { label: "Open", onClick: () => openItem(id) });
  };

  const deleteForever = async (spaceId: string | undefined, id: string) => {
    const ctx = spaceId ? ctxFor(spaceId) : getData();
    if (!ctx) return;
    const { cloudPending } = await deleteWorkspaceItemForever(ctx, id);
    if (cloudPending) toast.info("Deleted here. Cloud cleanup will finish when you reconnect.");
    else toast.info("Deleted permanently");
  };

  const renderRows = (items: WorkspaceItem[], spaceId?: string) =>
    items.map((i) => (
      <div key={i.id} className={s.row}>
        {i.type === "folder" ? <Folder size={14} /> : i.type === "tasklist" ? <SquareCheckBig size={14} /> : <FileText size={14} />}
        <span className={s.rowTitle}>{i.title || "Untitled"}</span>
        <span className={s.rowDate}>{new Date(i.trashedAt!).toLocaleDateString()}</span>
        <Button size="sm" variant="ghost" onClick={() => restore(spaceId, i.id, i.title)}>
          <RotateCcw size={12} /> Restore
        </Button>
        <IconButton label="Delete forever" onClick={() => void deleteForever(spaceId, i.id)}>
          <Trash2 size={13} />
        </IconButton>
      </div>
    ));

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Portal>
        <Dialog.Overlay className={s.overlay} />
        <Dialog.Content className={s.dialog}>
          <div className={s.header}>
            <Dialog.Title className={s.title}>{tasks ? "Task list trash" : "Trash"}</Dialog.Title>
            <Dialog.Close asChild>
              <IconButton label="Close">
                <X size={14} />
              </IconButton>
            </Dialog.Close>
          </div>
          <div className={s.hint}>Items delete themselves permanently after {TRASH_RETENTION_DAYS} days.</div>
          <div className={s.list}>
            {personalTrash.length === 0 && ownedSections.length === 0 ? (
              <div className={s.empty}>Trash is empty.</div>
            ) : (
              <>
                {renderRows(personalTrash)}
                {ownedSections.map(({ meta, trashed }) => (
                  <div key={meta.id}>
                    <div className={s.hint} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span
                        style={{
                          display: "inline-block",
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          background: sharedAccent(meta.colorKey),
                        }}
                      />
                      {meta.title} (shared, owner only)
                    </div>
                    {renderRows(trashed, meta.id)}
                  </div>
                ))}
              </>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
